import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getCookie } from '../../utils/cookies';
import { ACCOUNTS_COOKIE_KEY, ACTIVE_ACCOUNT_COOKIE_KEY } from '../../utils/constants';
import { switchAccount } from '../../redux/auth/authReducer';
import './AuthAccountSwitcher.css';

const UserAvatar = ({ src, firstName, email, className = "" }) => {
    const [imgError, setImgError] = useState(false);

    if (src && !imgError) {
        return (
            <img
                src={src}
                alt="Profile"
                className={`user-avatar ${className}`}
                onError={() => setImgError(true)}
            />
        );
    }

    // Fallback initials
    const safeName = firstName || email || "?";
    const initial = safeName.charAt(0).toUpperCase();

    // Generate a consistent background color based on name/email length? (Optional, kept simple gray for now)
    return <div className={`user-avatar ${className}`}>{initial}</div>;
};

export default function AuthAccountSwitcher() {
    const dispatch = useDispatch();
    const [accounts, setAccounts] = useState([]);
    const [activeId, setActiveId] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const updateAccounts = () => {
            const cookieVal = getCookie(ACCOUNTS_COOKIE_KEY);
            const activeVal = getCookie(ACTIVE_ACCOUNT_COOKIE_KEY);
            if (cookieVal) {
                try {
                    const parsed = JSON.parse(cookieVal);
                    if (Array.isArray(parsed)) {
                        setAccounts(parsed);
                    }
                } catch (e) { setAccounts([]); }
            }
            if (activeVal) setActiveId(activeVal);
        };
        updateAccounts();
    }, []);

    const handleSelect = (id) => {
        if (id && id.toString() !== activeId?.toString()) {
            dispatch(switchAccount(id));
            window.location.reload();
        }
        setIsOpen(false);
    };

    const handleAddAccount = () => {
        window.location.href = "/login?add_account=true";
    };

    const activeAccount = accounts.find(acc => acc.id?.toString() === activeId?.toString()) || {};

    return (
        <div className="auth-switcher-container" ref={containerRef}>
            <div
                className="auth-switcher-trigger"
                onClick={() => setIsOpen(!isOpen)}
                title={activeAccount.email || "Switch Account"}
            >
                <UserAvatar
                    src={activeAccount.profile_pic}
                    firstName={activeAccount.first_name}
                    email={activeAccount.email}
                />
            </div>

            {isOpen && (
                <div className="auth-dropdown-menu">
                    {accounts.map(acc => (
                        <button
                            key={acc.id}
                            className={`auth-account-item ${acc.id?.toString() === activeId?.toString() ? 'active' : ''}`}
                            onClick={() => handleSelect(acc.id)}
                        >
                            <UserAvatar
                                src={acc.profile_pic}
                                firstName={acc.first_name}
                                email={acc.email}
                                className="user-avatar-small"
                            />
                            <div className="account-details">
                                <span className="user-name">
                                    {acc.first_name ? `${acc.first_name} ${acc.last_name || ''}` : (acc.username || acc.email?.split('@')[0])}
                                </span>
                                <span className="user-email">{acc.email}</span>
                            </div>
                            {acc.id?.toString() === activeId?.toString() && (
                                <svg className="check-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            )}
                        </button>
                    ))}

                    <div className="auth-dropdown-divider" />

                    <button className="add-account-btn" onClick={handleAddAccount}>
                        <div className="plus-icon">
                            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                        </div>
                        Add another account
                    </button>
                </div>
            )}
        </div>
    );
}
